import { AdminPanelHeader } from './AdminPanelHeader';
import { ChannelNameInputField } from './ChannelNameInputField';
import { ChannelUsersList } from './ChannelUsersList';
import { UserList } from './UserList';

import { useWorkspaceController } from '../../context/WorkspaceController';
import { useAdminPanelFormState } from './context/AdminPanelFormContext';
import { Delete, Save } from '@mui/icons-material';
import { Box, Button, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import type { Channel as StreamChannel, UserResponse } from 'stream-chat';
import type { StreamChatType } from '../../types';

type EditChannelProps = {
  channel?: StreamChannel<StreamChatType> | null;
};

export const EditChannel = ({ channel }: EditChannelProps) => {
  const { closeAdminPanel } = useWorkspaceController();
  const { name, handleInputChange, handleSubmit, errors } = useAdminPanelFormState();
  const barnUser = useSelector((state: any) => state.user.barnUser);

  if (!channel || !channel.type) {
    closeAdminPanel();
    return <Box />;
  }

  const roles: string[] = barnUser?.roles || [];
  const channelCreator = channel.data?.created_by as (UserResponse<StreamChatType> & { auth0_user_id?: string }) | undefined;
  const isChannelCreator = Boolean(barnUser?.auth0UserId)
    && channelCreator?.auth0_user_id === barnUser.auth0UserId;
  const isAdminOrOwner = roles.includes('admin') || roles.includes('owner');
  const isSameTenant = channel.data?.team && barnUser?.barn?.tenant
    ? channel.data.team === barnUser.barn.tenant
    : false;
  const canDeleteChannel = (channel.type === 'messaging' && isChannelCreator)
    || (isAdminOrOwner && isSameTenant);

  return (
    <div className='admin-panel__form'>
      <AdminPanelHeader onClose={closeAdminPanel} title='Edit Channel' />
      <ChannelNameInputField name={name} error={errors.name} onChange={handleInputChange} />
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: channel.type === 'messaging' ? 'column' : { xs: 'column', md: 'row' },
          gap: 1,
          minHeight: 0,
          position: 'relative',
          alignItems: 'stretch',
          overflow: 'auto',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <ChannelUsersList />
        </Box>
        {channel.type === 'team' && (
          <Box sx={{ flex: 1 }}>
            <UserList />
          </Box>
        )}
      </Box>
      <Box sx={{ textAlign: 'right', m: 2 }}>
        {canDeleteChannel && (
          <Tooltip title="Only the creator of a channel can delete it">
            <Button
              endIcon={<Delete />}
              color="error"
              onClick={() => { void channel.delete(); }}
              sx={{ mr: 2 }}
            >
              Delete Channel
            </Button>
          </Tooltip>
        )}
        <Button endIcon={<Save />} variant="contained" onClick={handleSubmit}>Save</Button>
      </Box>
    </div>
  );
};
